import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import SprNumberCard from "./spr-number-card";
import "./spr-number.css";

const DRAWER_WIDTH_KEY = "planOnline.sprNumber.cardDrawer.width";
const MIN_WIDTH = 520;
const MAX_WIDTH = 960;
const DEFAULT_WIDTH = 640;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer карточки номера (SprNumber). Delphi: UESprNumber.
 */
const SprNumber = ({
  open,
  idNumber,
  isNew = false,
  isGroup = false,
  idUpper,
  parentOptions,
  onClose,
  onSaved
}) => {
  const [size, setSize] = useState(loadWidth);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const title = isGroup
    ? isNew
      ? "Новая группа"
      : `Группа${idNumber != null ? ` №${idNumber}` : ""}`
    : isNew
      ? "Новый номер"
      : `Номер${idNumber != null ? ` №${idNumber}` : ""}`;

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={maxAllowedWidth()}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={false}
      rootClassName="spr-number-drawer"
      extra={
        <Button
          type="text"
          className="spr-number-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idNumber != null || isNew) ? (
        <SprNumberCard
          idNumber={idNumber}
          isNew={isNew}
          isGroup={isGroup}
          idUpper={idUpper}
          open={open}
          parentOptions={parentOptions}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprNumber;
